import { BellOff, Check, CheckCheck, ExternalLink, Hash, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';
import { LineChart } from '@/components/charts/LineChart';
import {
  Avatar,
  Badge,
  Button,
  CapacityBar,
  Checkbox,
  EntityChip,
  type EntityKind,
  EntityPeek,
  IconButton,
  Input,
  Kbd,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stat,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  Timeline,
  cn,
} from '@/components/ui';
import {
  cpuSeries,
  mentions,
  problems,
  related,
  severityLabels,
  severityPill,
  tasks,
  timeline,
  trends,
} from './fixtures';

/*
  Экран «Узел cn338» — шаблон «Объект» (docs/design-patterns-v3.md §5): шапка с сущностью и статусом,
  вкладки видов, ряд метрик, график, таблица проблем, хронология, форма, задачи, упоминания, связи.
  Одно главное действие на экран — «Подтвердить всё». Всё — из примитивов и токенов.
*/

const filterChips = [
  { label: 'Все', count: 12, active: true },
  { label: 'High', count: 2 },
  { label: 'Average', count: 3 },
  { label: 'Warning', count: 5 },
  { label: 'Без подтверждения', count: 7 },
];

/** Что показать в карточке живого имени (R26) — по типу объекта. */
function peekFor(kind: EntityKind, name: string) {
  if (kind === 'vm') {
    return {
      kind,
      name,
      subtitle: 'Виртуальная машина · узел host-338',
      state: { label: 'Работает', variant: 'success' as const },
      metrics: [
        { label: 'CPU', value: '96 %', trend: trends.cpu },
        { label: 'Память', value: '74 %', trend: trends.mem },
        { label: 'Аптайм', value: '19 сут' },
      ],
      note: 'Больше всех потребляет на узле',
    };
  }
  return {
    kind,
    name,
    subtitle: 'Гипервизор · Площадка А · Proxmox 8.2',
    state: { label: 'Проблема', variant: 'urgent' as const },
    metrics: [
      { label: 'CPU', value: '34 %', trend: trends.cpu },
      { label: 'Память', value: '61 %', trend: trends.mem },
      { label: 'Машин', value: '48' },
    ],
    note: '2 проблемы за сутки',
  };
}

export function NodeOverview({ dense }: { dense: boolean }) {
  const series = useMemo(() => [{ id: 'cpu', label: 'CPU, %', points: cpuSeries() }], []);
  // Засечки событий (R29): всплеск на графике объясняется сам — видно, после чего началось.
  const chartEvents = useMemo(() => {
    const start = series[0].points[0]?.x;
    if (start === undefined) return [];
    const h = 3600 * 1000;
    return [
      { x: start + 2 * h, kind: 'maintenance' as const, label: 'Окно обслуживания, стойка R-A' },
      { x: start + 9 * h, kind: 'migration' as const, label: 'Переезд двух машин с host-341' },
      { x: start + 17.4 * h, kind: 'deploy' as const, label: 'Выкладка 1.14.2, агент метрик' },
    ];
  }, [series]);
  const [range, setRange] = useState('24h');
  const [selected, setSelected] = useState<string[]>([]);
  const [notifySlack, setNotifySlack] = useState(true);
  const [notifyTg, setNotifyTg] = useState(false);
  const [done, setDone] = useState<Record<string, boolean>>(
    Object.fromEntries(tasks.map((t) => [t.id, t.done])),
  );

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const pad = dense ? 'p-4' : 'p-5';

  return (
    <div className={cn('flex flex-col', dense ? 'gap-4 px-6 py-4' : 'gap-6 px-8 py-6')}>
      {/* Шапка объекта */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <EntityChip kind="location" name="Площадка А" meta="DC-1" />
            <EntityChip kind="host" name="Стойка R-A" meta="U17" />
            <EntityChip kind="user" name="Михаил" meta="владелец" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="type-title text-fg">host-338</h1>
            <Badge variant="urgent" solid>
              High · 2 проблемы
            </Badge>
            <Badge variant="neutral">Proxmox VE 8.2</Badge>
          </div>
          <p className="text-sm text-fg-muted">
            48 VPS · аптайм 41 д · 2 × Xeon Gold 6338, 512 ГБ · последнее изменение 2 ч назад (Михаил)
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" rightIcon={<ExternalLink />}>
            Открыть в центре мониторинга
          </Button>
          <Button variant="secondary" leftIcon={<Wrench />}>
            Обслуживание
          </Button>
          <Button leftIcon={<CheckCheck />}>Подтвердить всё</Button>
        </div>
      </header>

      {/* Виды объекта */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Обзор</TabsTrigger>
          <TabsTrigger value="problems" count={12}>
            Проблемы
          </TabsTrigger>
          <TabsTrigger value="vms" count={48}>
            VPS
          </TabsTrigger>
          <TabsTrigger value="graphs">Графики</TabsTrigger>
          <TabsTrigger value="history">История</TabsTrigger>
          <TabsTrigger value="settings">Настройки</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Метрики */}
      <div className={cn('grid grid-cols-2 xl:grid-cols-5', dense ? 'gap-3' : 'gap-4')}>
        <Stat
          label="CPU"
          value="34"
          unit="%"
          delta={{ value: 6, label: 'за час', goodDirection: 'down' }}
          trend={trends.cpu}
          trendTone="brand"
          trendFill="background"
        />
        <Stat
          label="Load average"
          value="0,42"
          delta={{ value: -7.9, label: 'после пика 8,4', goodDirection: 'down' }}
          trend={trends.la}
          trendFill="background"
        />
        <Stat
          label="Память"
          value="61"
          unit="%"
          delta={{ value: 1, label: 'за сутки', goodDirection: 'down' }}
          trend={trends.mem}
          trendFill="background"
        />
        <Stat
          label="Сеть"
          value="1,2"
          unit="Гбит/с"
          delta={{ value: 0.1, label: 'за час' }}
          trend={trends.net}
          trendFill="background"
        />
        <Stat
          label="Ёмкость"
          value="48"
          unit="из 60 VPS"
          footer={<CapacityBar value={80} className="w-full" />}
        />
      </div>

      <div className={cn('grid grid-cols-1 xl:grid-cols-3', dense ? 'gap-4' : 'gap-6')}>
        {/* Левая колонка */}
        <div className={cn('flex min-w-0 flex-col xl:col-span-2', dense ? 'gap-4' : 'gap-6')}>
          <section className={cn('rounded-lg border border-line bg-card', pad)}>
            <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="type-subheading text-fg">Нагрузка CPU</h2>
                <p className="text-xs text-fg-muted">за 24 часа · порог 80 % · среднее 31 %</p>
              </div>
              <Tabs variant="pill" value={range} onValueChange={setRange}>
                <TabsList>
                  <TabsTrigger value="1h">1 ч</TabsTrigger>
                  <TabsTrigger value="24h">24 ч</TabsTrigger>
                  <TabsTrigger value="7d">7 д</TabsTrigger>
                </TabsList>
              </Tabs>
            </header>
            <LineChart
              series={series}
              events={chartEvents}
              height={dense ? 200 : 240}
              unit="%"
              yMax={100}
              threshold={{ value: 80, label: 'порог 80 %' }}
            />
          </section>

          <section className="rounded-lg border border-line bg-card">
            <header
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 border-b border-line',
                dense ? 'px-4 py-3' : 'px-5 py-4',
              )}
            >
              <div className="flex items-center gap-3">
                <h2 className="type-subheading text-fg">Проблемы</h2>
                <span className="text-xs text-fg-subtle">обновлено 12 с назад</span>
              </div>
              <div className="w-56">
                <Input placeholder="Фильтр по тексту" aria-label="Фильтр" />
              </div>
            </header>
            <div
              className={cn(
                'flex flex-wrap items-center gap-2 border-b border-line',
                dense ? 'px-4 py-2' : 'px-5 py-3',
              )}
            >
              {filterChips.map((c) => (
                <button
                  key={c.label}
                  type="button"
                  className={cn(
                    'inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors duration-fast',
                    c.active
                      ? 'border-transparent bg-fg text-fg-inverse'
                      : 'border-line bg-card text-fg-muted hover:border-line-strong hover:text-fg',
                  )}
                >
                  {c.label}
                  <span className={cn('tabular-nums', c.active ? 'opacity-70' : 'text-fg-subtle')}>
                    {c.count}
                  </span>
                </button>
              ))}
            </div>
            <Table dense={dense}>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      aria-label="Выбрать все"
                      checked={
                        selected.length === 0
                          ? false
                          : selected.length === problems.length
                            ? true
                            : 'indeterminate'
                      }
                      onCheckedChange={(v) => setSelected(v ? problems.map((p) => p.id) : [])}
                    />
                  </TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Проблема</TableHead>
                  <TableHead>Объект</TableHead>
                  <TableHead numeric>Длится</TableHead>
                  <TableHead>Подтверждение</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {problems.map((p) => (
                  <TableRow key={p.id} interactive selected={selected.includes(p.id)}>
                    <TableCell>
                      <Checkbox
                        aria-label={`Выбрать ${p.name}`}
                        checked={selected.includes(p.id)}
                        onCheckedChange={() => toggle(p.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-flex rounded-full px-2 py-0.5 text-2xs font-semibold',
                          severityPill[p.sev],
                        )}
                      >
                        {severityLabels[p.sev]}
                      </span>
                    </TableCell>
                    <TableCell className="min-w-64">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium">{p.name}</span>
                        <span className="text-2xs text-fg-subtle">{p.tags.join(' · ')}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <EntityPeek {...peekFor(p.target.kind, p.target.name)}>
                        <span className="inline-flex">
                          <EntityChip kind={p.target.kind} name={p.target.name} />
                        </span>
                      </EntityPeek>
                    </TableCell>
                    <TableCell numeric muted className="whitespace-nowrap">
                      {p.since}
                    </TableCell>
                    <TableCell muted>
                      {p.acked ? (
                        <span className="inline-flex items-center gap-1 text-xs text-success-fg">
                          <Check className="size-3.5" /> {p.ackBy}
                        </span>
                      ) : (
                        <span className="text-xs text-fg-subtle">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1 opacity-0 transition-opacity duration-fast group-hover/row:opacity-100 focus-within:opacity-100">
                        <IconButton aria-label="Подтвердить" size="sm">
                          <Check />
                        </IconButton>
                        <IconButton aria-label="Заглушить" size="sm">
                          <BellOff />
                        </IconButton>
                        <IconButton aria-label="Открыть" size="sm">
                          <ExternalLink />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <footer
              className={cn(
                'flex flex-wrap items-center justify-between gap-3 border-t border-line text-xs text-fg-subtle',
                dense ? 'px-4 py-2' : 'px-5 py-3',
              )}
            >
              <span>Показано 5 из 12</span>
              <span className="inline-flex items-center gap-1.5">
                <Kbd>j</Kbd>
                <Kbd>k</Kbd> перемещение · <Kbd>a</Kbd> подтвердить · <Kbd>s</Kbd> заглушить · <Kbd>↵</Kbd>{' '}
                детали
              </span>
            </footer>
          </section>
        </div>

        {/* Правая колонка */}
        <div className={cn('flex min-w-0 flex-col', dense ? 'gap-4' : 'gap-6')}>
          <section className={cn('rounded-lg border border-line bg-card', pad)}>
            <h2 className="mb-4 type-subheading text-fg">Хронология</h2>
            <Timeline items={timeline} />
          </section>

          <section className={cn('rounded-lg border border-line bg-card', pad)}>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="type-subheading text-fg">Уведомления по узлу</h2>
              <span className="inline-flex items-center gap-1 text-2xs text-success-fg">
                <Check className="size-3" /> сохранено 12:32
              </span>
            </div>
            <div className="flex flex-col gap-4">
              <Switch
                checked={notifySlack}
                onCheckedChange={setNotifySlack}
                label="Slack #alerts-infra"
                description="все триггеры High и выше"
              />
              <Switch
                checked={notifyTg}
                onCheckedChange={setNotifyTg}
                label="Telegram дежурному"
                description="только Disaster, с эскалацией через 10 мин"
              />
              <div className="flex flex-col gap-2">
                <span className="text-xs font-medium text-fg-muted">Уровни в дайджест</span>
                <Checkbox defaultChecked label="High и Disaster" />
                <Checkbox defaultChecked label="Average" />
                <Checkbox label="Warning" description="шумно на этом узле" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <span className="text-xs font-medium text-fg-muted">Окно тишины</span>
                  <Select defaultValue="1h">
                    <SelectTrigger aria-label="Окно тишины">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15m">15 минут</SelectItem>
                      <SelectItem value="1h">1 час</SelectItem>
                      <SelectItem value="4h">4 часа</SelectItem>
                      <SelectItem value="morning">До утра</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Input label="Порог LA" defaultValue="8,0" helper="на ядро" />
              </div>
            </div>
          </section>

          <section className={cn('rounded-lg border border-line bg-card', pad)}>
            <h2 className="mb-3 type-subheading text-fg">Задачи</h2>
            <ul className="flex flex-col divide-y divide-line">
              {tasks.map((t) => (
                <li key={t.id} className="flex items-center gap-3 py-2">
                  <Checkbox
                    aria-label={t.title}
                    checked={!!done[t.id]}
                    onCheckedChange={(v) => setDone((d) => ({ ...d, [t.id]: !!v }))}
                  />
                  <span
                    className={cn(
                      'min-w-0 flex-1 truncate text-sm',
                      done[t.id] ? 'text-fg-subtle line-through' : 'text-fg',
                    )}
                  >
                    {t.title}
                  </span>
                  {t.linked && <EntityChip kind="ticket" name={t.linked} />}
                  <Badge variant={t.due === 'сегодня' ? 'warn' : 'neutral'}>{t.due}</Badge>
                  <Avatar name={t.assignee} size="xs" />
                </li>
              ))}
            </ul>
          </section>

          <section className={cn('rounded-lg border border-line bg-card', pad)}>
            <h2 className="mb-3 type-subheading text-fg">Упоминания</h2>
            <ul className="flex flex-col divide-y divide-line">
              {mentions.map((m) => (
                <li key={m.id} className="flex gap-3 py-2">
                  <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-sm bg-muted text-fg-subtle">
                    <Hash className="size-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-xs font-medium text-fg">
                        {m.who} <span className="font-normal text-fg-subtle">в {m.channel}</span>
                      </span>
                      <span className="shrink-0 font-mono text-2xs text-fg-subtle">{m.time}</span>
                    </div>
                    <p className={cn('text-sm', m.unread ? 'text-fg' : 'text-fg-muted')}>{m.text}</p>
                  </div>
                  {m.unread && (
                    <span
                      className="mt-2 size-1.5 shrink-0 rounded-full bg-brand"
                      aria-label="не прочитано"
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>

          <section className={cn('rounded-lg border border-line bg-card', pad)}>
            <h2 className="mb-3 type-subheading text-fg">Связано</h2>
            <div className="flex flex-wrap gap-2">
              {related.map((r) => (
                <EntityChip key={r.name} kind={r.kind} name={r.name} meta={r.meta} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
